import React from 'react';
import { Checkbox, FormControlLabel, FormGroup, Typography } from '@mui/material';
import { BaseField } from './BaseField';
import type { BaseFieldProps } from './types';

/**
 * Multiple choice field component that handles:
 * - multiple_choice (form-builder)
 * - checkbox (legacy)
 */
export const MultipleChoiceField: React.FC<BaseFieldProps> = ({
  field,
  value = [],
  onChange,
  onBlur,
  mode = 'edit',
  error,
  touched,
  sx,
  showLabel = true,
  disabled,
}) => {
  // Ensure value is always an array
  const selectedValues = Array.isArray(value) ? value : [];
  
  // Extract field properties based on field format
  const label = 'label' in field ? field.label : '';
  
  // Extract options based on field format
  let options: Array<{ label: string; value: string }> = [];
  
  if ('options' in field && field.options) {
    options = field.options.map((opt: any) => {
      if (typeof opt === 'string') {
        return { label: opt, value: opt };
      } else if ('label' in opt && 'value' in opt) {
        return { label: opt.label, value: String(opt.value) };
      } else if ('text' in opt) {
        return { label: opt.text, value: String(opt.value || opt.id || opt.text) };
      }
      return { label: String(opt), value: String(opt) };
    });
  }

  const handleChange = (optionValue: string, checked: boolean) => {
    if (!onChange) return;
    
    const newValues = checked
      ? [...selectedValues, optionValue]
      : selectedValues.filter(v => v !== optionValue);
    
    onChange(newValues);
  };

  // Handle display mode
  if (mode === 'display') {
    const selectedLabels = options
      .filter(opt => selectedValues.includes(opt.value))
      .map(opt => opt.label);
    
    return (
      <BaseField field={field} mode={mode} sx={sx} showLabel={showLabel}>
        <Typography variant="body2">
          {selectedLabels.length > 0 ? selectedLabels.join(', ') : '-'}
        </Typography>
      </BaseField>
    );
  }

  // Handle edit/preview modes
  return (
    <BaseField
      field={field}
      mode={mode}
      error={error}
      touched={touched}
      sx={sx}
      showLabel={showLabel}
      disabled={disabled}
    >
      <FormGroup onBlur={onBlur}>
        {options.map((option, index) => (
          <FormControlLabel
            key={index}
            control={
              <Checkbox
                checked={selectedValues.includes(option.value)}
                onChange={(e) => handleChange(option.value, e.target.checked)}
                size="small"
              />
            }
            label={option.label}
            disabled={disabled || mode === 'preview'}
          />
        ))}
      </FormGroup>
    </BaseField>
  );
};